/**
 * chunkRecovery — recupera de um chunk que sumiu do servidor durante um deploy (LDG-0126).
 *
 * O cenário: o `index.html` é servido com `no-store`, então é sempre fresco, mas os bundles que
 * ele referencia mudam de nome a cada build. Durante um rolling update, pod velho e pod novo
 * ficam Ready ao mesmo tempo e o balanceador pode entregar a página de um e o chunk do outro —
 * o nginx responde 404 duro (`try_files $uri =404`) e o usuário vê tela branca ou "Algo deu
 * errado".
 *
 * A defesa PRINCIPAL é a afinidade de sessão no balanceador (BackendConfig do GKE, ver
 * `platform/helm/charts/azim-service/templates/backendconfig.yaml`), porque ela é a única que
 * cobre o import ESTÁTICO: os chunks do `manualChunks` se importam por nome, e um 404 em
 * `design-system-<hash>.js` falha na fase de LINK, antes de qualquer código ser avaliado — nenhum
 * script de recuperação chega a rodar.
 *
 * Este módulo é a rede para o que a afinidade não cobre: o import DINÂMICO de rota lazy, quando o
 * pod ao qual o cookie prendia o cliente é removido no fim do rollout.
 *
 * A regra que não pode ser violada: **uma tentativa**. Recarregar em laço trocaria uma falha de
 * um a três minutos, autocurável, por um navegador travado — seria pior que o defeito. A
 * evidência da tentativa vive no `sessionStorage` porque precisa sobreviver ao próprio reload;
 * e é limpa no primeiro import bem-sucedido, senão a guarda antiloop viraria uma guarda
 * anti-recuperação em todos os deploys seguintes daquela aba.
 */

const CHAVE_TENTATIVA = "azim.chunkRecovery.attempted";

/**
 * Formas com que os browsers relatam "não consegui buscar/avaliar este módulo". Deliberadamente
 * abrangente: um erro de chunk não reconhecido volta a virar tela branca, enquanto um falso
 * positivo custa um único reload — e só quando o import já falhou de qualquer forma.
 */
const SINTOMAS_DE_CHUNK = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "unable to preload css",
  "failed to load module script",
  "expected a javascript module script",
];

export interface RecoveryOptions {
  /** Como recarregar a página. Injetável para o teste observar sem navegar de verdade. */
  readonly reload: () => void;
}

function ehErroDeChunk(erro: unknown): boolean {
  if (!(erro instanceof Error)) return false;
  const mensagem = erro.message.toLowerCase();
  return SINTOMAS_DE_CHUNK.some((s) => mensagem.includes(s));
}

function jaTentou(): boolean {
  try {
    return sessionStorage.getItem(CHAVE_TENTATIVA) !== null;
  } catch {
    // sessionStorage indisponível (modo privado, storage bloqueado). Sem memória da tentativa,
    // o único comportamento seguro é NÃO recarregar — o laço é o risco maior.
    return true;
  }
}

function marcarTentativa(): void {
  try {
    sessionStorage.setItem(CHAVE_TENTATIVA, "1");
  } catch {
    /* sem storage: a leitura acima já devolve `true` e inibe o reload */
  }
}

function limparTentativa(): void {
  try {
    sessionStorage.removeItem(CHAVE_TENTATIVA);
  } catch {
    /* ignore */
  }
}

/**
 * Executa um import dinâmico e, se ele falhar por chunk ausente, recarrega a página uma vez.
 *
 * O erro é SEMPRE relançado: quem chama continua vendo a falha. Quando o reload acontece, a
 * página some antes de o erro ser tratado; quando não acontece — porque já se tentou, ou porque
 * o erro não é de chunk —, o `ErrorBoundary` recebe o erro como sempre recebeu.
 */
export async function recoverableImport<T>(
  load: () => Promise<T>,
  options: RecoveryOptions,
): Promise<T> {
  try {
    const modulo = await load();
    // Chegou aqui: os assets desta versão estão acessíveis. A marca pode sair, para que o
    // PRÓXIMO deploy também possa se recuperar nesta mesma aba.
    limparTentativa();
    return modulo;
  } catch (erro) {
    if (ehErroDeChunk(erro) && !jaTentou()) {
      marcarTentativa();
      options.reload();
    }
    throw erro;
  }
}

/** Reset de estado entre casos de teste. Não é usado em produção. */
export function __resetRecoveryStateForTests(options?: { keepSessionStorage?: boolean }): void {
  if (!options?.keepSessionStorage) limparTentativa();
}
